if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.ifMediaStack = this.hasAttribute("data-media-stack");
        this.lastVariantMediaId = parseInt(
          this.querySelector(".is-current-variant-media")?.dataset.mediaId,
        ); // 当前选择的产品变体对应的MediaId
        this.lastVariantGroup = this.dataset.currentMediaGroup || null; // 当前选择的产品变体对应的媒体分组
        this.mediaGroupOptionIndex =
          this.dataset.mediaGroupOptionIndex == null
            ? null
            : parseInt(this.dataset.mediaGroupOptionIndex); // 用于分组的颜色选项索引

        if (!this.thumbnails) return;
        this.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((thumbnail) => {
            thumbnail.addEventListener(
              "click",
              this.onThumbnailClick.bind(this),
            );
          });

        this.addEventListener("sliderChanged", this.onSlideChanged.bind(this));
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      /**
       * 当前展示的媒体分组，供相册弹窗同步使用
       * @returns {string|null}
       */
      get currentMediaGroup() {
        return this.lastVariantGroup;
      }

      /**
       * 媒体切换事件
       * @param event
       */
      onSlideChanged(event) {
        if (!event || !event.detail) return;

        const mediaId = event.detail.currentElement.dataset.mediaId;

        // 滚动缩略图到当前媒体
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return;

        if (!thumbnail.querySelector(".thumbnail-media").hasAttribute("aria-current")) {
          this.setActiveThumbnail(thumbnail);
        }
        if (this.thumbnails.initSliderStatus)
          this.thumbnails.moveElementToOptimalPosition(thumbnail); // 将缩略图元素移动到合适的位置
      }

      /**
       * 点击缩略图处理
       */
      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        this.setActiveThumbnail(thumbnail);

        this.changeMedia(thumbnail.getAttribute("data-target"));
      }

      /**
       * 更新相册，如果需要隐藏其它变体图片，需要重新安装轮播组件
       * @param currentVariantMediaId 当前变体的 Featured Media Id，变体没有关联媒体时为 null
       * @param variant 当前变体对象，用于变体没有关联媒体时定位分组
       */
      updateGallery(currentVariantMediaId, variant = null) {
        const groupName = this.getVariantGroup(currentVariantMediaId, variant);
        const mediaChanged =
          currentVariantMediaId != null &&
          currentVariantMediaId !== this.lastVariantMediaId;
        const groupChanged = groupName !== this.lastVariantGroup;

        if (!mediaChanged && !groupChanged) return;

        if (currentVariantMediaId != null)
          this.lastVariantMediaId = currentVariantMediaId;
        this.lastVariantGroup = groupName;

        // 修改当前变体关联的媒体
        if (mediaChanged) {
          this.viewer
            .querySelectorAll(".is-current-variant-media")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media"),
            );
          this.viewer
            .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
            .forEach((element) =>
              element.classList.add("is-current-variant-media"),
            );

          if (this.thumbnails) {
            this.thumbnails
              .querySelector(".is-current-variant-thumbnail")
              ?.classList.remove("is-current-variant-thumbnail");
            this.thumbnails
              .querySelector(`[data-target="${currentVariantMediaId}"]`)
              ?.classList.add("is-current-variant-thumbnail");
          }
        }

        // 如果开启了隐藏其它变体设置
        if (this.hasAttribute("data-hide-variants")) {
          // 当前分组没有对应媒体时（例如该颜色未配图），展示全部媒体
          const showAll = !this.hasGroupMedia(groupName);

          // 切换当前变体的媒体组
          this.toggleGroup(
            this.viewer,
            "data-media-group",
            "is-current-variant-media-group",
            groupName,
            showAll,
          );

          if (this.thumbnails) {
            this.toggleGroup(
              this.thumbnails,
              "data-thumbnail-group",
              "is-current-variant-thumbnail-group",
              groupName,
              showAll,
            );
          }

          // 如果启用了隐藏其它变体媒体，需要重新初始化轮播
          if (!this.ifMediaStack) {
            this.viewer.reInstall();
            if (this.thumbnails) this.thumbnails.reInstall();
          }
        }

        // 变体没有关联媒体时，切换到当前分组下的第一张可见媒体
        this.changeMedia(currentVariantMediaId ?? this.firstVisibleMediaId());

        // 手机端，当媒体切换的时候，返回页面顶部
        if (webvista.isMobileScreen()) {
          webvista.scrollElementToHeaderBottom(this, 16);
        }
      }

      /**
       * 计算当前变体所属的媒体分组
       * 优先取变体关联媒体上的分组，变体没有关联媒体时退回到变体的颜色选项值
       * @param currentVariantMediaId 当前变体的 Featured Media Id
       * @param variant 当前变体对象
       * @returns {string|null}
       */
      getVariantGroup(currentVariantMediaId, variant) {
        if (currentVariantMediaId != null) {
          const mediaElement = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          );
          if (mediaElement?.dataset.mediaGroup)
            return mediaElement.dataset.mediaGroup;
        }

        if (this.mediaGroupOptionIndex != null && variant?.options) {
          const variantColor = variant.options[this.mediaGroupOptionIndex];
          if (!variantColor) return null;

          // 兼容 alt 手动命名的 ###颜色 分组
          if (
            !this.hasGroupMedia(variantColor) &&
            this.hasGroupMedia(`###${variantColor}`)
          )
            return `###${variantColor}`;

          return variantColor;
        }

        return null;
      }

      /**
       * 当前分组下是否有媒体
       * @param groupName 当前分组名
       * @returns {boolean}
       */
      hasGroupMedia(groupName) {
        if (groupName == null) return false;

        return Array.from(
          this.viewer.querySelectorAll("[data-media-group]"),
        ).some(
          (element) => element.getAttribute("data-media-group") === groupName,
        );
      }

      /**
       * 切换分组的活跃状态，只有属于当前分组的元素才会展示
       * @param container 查找范围
       * @param attribute 分组属性名
       * @param activeClass 活跃分组的类名
       * @param groupName 当前分组名
       * @param showAll 是否忽略分组、展示全部媒体
       */
      toggleGroup(container, attribute, activeClass, groupName, showAll) {
        container.querySelectorAll(`[${attribute}]`).forEach((element) =>
          element.classList.toggle(
            activeClass,
            showAll ||
              (groupName != null &&
                element.getAttribute(attribute) === groupName),
          ),
        );
      }

      /**
       * 获取第一张可见媒体的 Id
       * @returns {string|null}
       */
      firstVisibleMediaId() {
        const visibleMedia = Array.from(
          this.viewer.querySelectorAll(".product-media-item"),
        ).find((element) => element.offsetParent !== null);

        return visibleMedia?.dataset.mediaId ?? null;
      }

      /**
       * 设置活跃媒体
       * @param mediaId
       */
      changeMedia(mediaId) {
        if (mediaId == null) return;

        const activeMedia = this.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );
        if (!activeMedia) return;

        if (this.ifMediaStack) {
          // 如果是 Stack 布局，需要将当前变体 Featured Media 放置最前面
          activeMedia.parentElement.prepend(activeMedia);
        } else {
          // 轮播模式，切换当前轮播
          if (this.viewer.initSliderStatus)
            this.viewer.slideByElement(activeMedia);
        }
      }

      /**
       * 设置活跃缩略图
       * @param thumbnail
       */
      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);
      }
    }
  );
}
